import React, { useEffect, useState } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Layers, PencilIcon, TrashIcon, XIcon } from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe } from 'types/UtilityTypes';

import {
  QuickQuoteInputs,
  ThreeGCompatibilityProfileItem,
  ThreeGProfileType,
} from '../../types';
import { CARD_ANIMATIONS as ANIMATIONS } from './cardAnimations';
import { useLTLProfiles } from './hooks/useLTLProfiles';

const FIELD_PATH = 'ltl.compatibilityProfiles';

const PROFILE_TYPE_OPTIONS: Array<{ value: ThreeGProfileType; label: string }> =
  [
    { value: 'Equipment', label: 'Equipment' },
    { value: 'Carrier', label: 'Carrier' },
    { value: 'Mode', label: 'Mode' },
  ];

interface ProfileCardProps {
  index: number;
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  onRemove: () => void;
  integrationId: Maybe<number>;
}

export function ProfileCard({
  index,
  formMethods,
  onRemove,
  integrationId,
}: ProfileCardProps) {
  const { watch, setValue } = formMethods;
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<Partial<ThreeGCompatibilityProfileItem>>>(null);

  const watchedProfile = watch(`${FIELD_PATH}.${index}`);
  const isReadOnly = Boolean(watchedProfile?.isReadOnly);
  const profileType = watchedProfile?.profileType as Maybe<ThreeGProfileType>;
  const { options: profileNameOptions, isLoading: isLoadingProfiles } =
    useLTLProfiles(integrationId, profileType ?? null);

  const isEmptyProfile =
    !watchedProfile?.profileType && !watchedProfile?.profileName;

  const hasBothProfileTypeAndName =
    Boolean(watchedProfile?.profileType) &&
    Boolean(watchedProfile?.profileName);

  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedProfile });
      setIsEditing(true);
    },
    save: () => {
      if (!hasBothProfileTypeAndName) return;
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        const id = watchedProfile?.id ?? originalValues.id ?? '';
        const fullSnapshot: ThreeGCompatibilityProfileItem = {
          id,
          profileType: originalValues.profileType,
          profileName: originalValues.profileName,
          profileNameLabel: originalValues.profileNameLabel,
        };
        setValue(
          `${FIELD_PATH}.${index}` as FieldPath<QuickQuoteInputs>,
          fullSnapshot,
          { shouldValidate: true }
        );
        setIsEditing(false);
        setOriginalValues(null);
      } else if (!hasBothProfileTypeAndName) {
        onRemove();
      } else {
        setIsEditing(false);
        setOriginalValues(null);
      }
    },
    remove: onRemove,
  };

  useEffect(() => {
    if (isReadOnly) {
      return;
    }
    if (isEmptyProfile && !isEditing) {
      setIsEditing(true);
    }
  }, [isEmptyProfile, isEditing, isReadOnly]);

  const renderActions = () => {
    if (isReadOnly) {
      return null;
    }

    return (
      <AnimatePresence mode='wait'>
        {isEditing ? (
          <motion.div
            key='editing-actions'
            {...ANIMATIONS.actions}
            className='flex gap-1.5'
          >
            {!isEmptyProfile ? (
              <>
                <span
                  className={
                    hasBothProfileTypeAndName
                      ? ''
                      : 'pointer-events-none opacity-50'
                  }
                  title={
                    hasBothProfileTypeAndName
                      ? undefined
                      : 'Select both Profile Type and Profile Name to save'
                  }
                >
                  <ActionButton
                    onClick={handlers.save}
                    className='hover:bg-info-200 text-info-600'
                    icon={CheckIcon}
                    aria-label='Save changes'
                  />
                </span>
                <ActionButton
                  onClick={handlers.discard}
                  className='hover:bg-error-200 text-error-600'
                  icon={XIcon}
                  aria-label='Discard changes'
                />
              </>
            ) : (
              <ActionButton
                onClick={handlers.remove}
                className='hover:bg-error-300 text-error-600'
                icon={TrashIcon}
                aria-label='Remove profile'
              />
            )}
          </motion.div>
        ) : (
          <motion.div
            key='view-actions'
            {...ANIMATIONS.actions}
            className='flex gap-1.5'
          >
            <ActionButton
              onClick={handlers.edit}
              className='hover:bg-info-300 text-info-600'
              icon={PencilIcon}
              aria-label='Edit profile'
            />
            <ActionButton
              onClick={handlers.remove}
              className='hover:bg-error-400 text-error-600'
              icon={TrashIcon}
              aria-label='Remove profile'
            />
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  const summaryParts: string[] = [];
  if (watchedProfile?.profileType) {
    summaryParts.push(watchedProfile.profileType);
  }

  if (watchedProfile?.profileNameLabel) {
    summaryParts.push(watchedProfile.profileNameLabel);
  }

  const summaryLabel =
    summaryParts.length > 0 ? summaryParts.join(' • ') : `Profile ${index + 1}`;

  const renderViewMode = () => (
    <motion.div
      key='view-mode'
      {...ANIMATIONS.toggle}
      className={
        isReadOnly ? 'overflow-hidden' : 'overflow-hidden cursor-pointer'
      }
      onClick={isReadOnly ? undefined : handlers.edit}
      onKeyDown={
        isReadOnly
          ? undefined
          : (e: React.KeyboardEvent) => e.key === 'Enter' && handlers.edit()
      }
      role={isReadOnly ? undefined : 'button'}
      tabIndex={isReadOnly ? undefined : 0}
      aria-label={isReadOnly ? undefined : 'Click to edit profile'}
    >
      <Flex justify='between' align='center' className='min-w-0'>
        <Flex align='center' gap='sm' className='flex-1 min-w-0 mr-2'>
          <Layers className='h-4 w-4 text-neutral-500 shrink-0' />
          <Typography
            variant='body-sm'
            weight='medium'
            className='truncate'
            title={summaryLabel}
          >
            {summaryLabel}
          </Typography>
        </Flex>
        <div onClick={(e) => e.stopPropagation()} className='shrink-0'>
          {renderActions()}
        </div>
      </Flex>
    </motion.div>
  );

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      <Flex justify='between' align='center' className='mb-3'>
        <Flex align='center' gap='sm'>
          <Typography variant='body-sm' weight='medium'>
            Edit Profile
          </Typography>
        </Flex>
        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>

      <Grid cols='2' gap='lg' className='w-full mx-0'>
        <div className='w-full'>
          <Label name={`${FIELD_PATH}.${index}.profileType`}>
            Profile Type
          </Label>
          <Select
            value={watch(`${FIELD_PATH}.${index}.profileType`) ?? ''}
            onChange={(value) => {
              const strValue = value != null ? String(value) : '';
              setValue(
                `${FIELD_PATH}.${index}.profileType` as FieldPath<QuickQuoteInputs>,
                strValue as ThreeGProfileType
              );
              setValue(
                `${FIELD_PATH}.${index}.profileName` as FieldPath<QuickQuoteInputs>,
                ''
              );
              setValue(
                `${FIELD_PATH}.${index}.profileNameLabel` as FieldPath<QuickQuoteInputs>,
                ''
              );
            }}
            options={PROFILE_TYPE_OPTIONS}
            placeholder='Select profile type'
            className='w-full mt-1'
          />
        </div>

        <div className='w-full'>
          <Label name={`${FIELD_PATH}.${index}.profileName`}>
            Profile Name
          </Label>
          <Select
            value={watch(`${FIELD_PATH}.${index}.profileName`) ?? ''}
            onChange={(value) => {
              const strValue = value != null ? String(value) : '';
              setValue(
                `${FIELD_PATH}.${index}.profileName` as FieldPath<QuickQuoteInputs>,
                strValue
              );
              const selected = profileNameOptions.find(
                (opt) => String(opt.value) === strValue
              );
              setValue(
                `${FIELD_PATH}.${index}.profileNameLabel` as FieldPath<QuickQuoteInputs>,
                selected?.label ?? ''
              );
            }}
            options={profileNameOptions}
            showSearch
            placeholder={
              isLoadingProfiles
                ? 'Loading...'
                : profileType
                  ? 'Select or search profile'
                  : 'Select profile type first'
            }
            disabled={isLoadingProfiles || !profileType}
            className='w-full mt-1'
          />
        </div>
      </Grid>
    </motion.div>
  );

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className='w-full overflow-hidden rounded-md border border-neutral-400 shadow-sm relative p-3 mb-2'
    >
      <AnimatePresence mode='wait'>
        {isEditing && !isReadOnly ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
